import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Divider, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  IconButton, 
  Snackbar, 
  Alert,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './components/LanguageSwitcher';
import { getUserInfo, updatePublicName } from './services/api';
import { useAuth } from './contexts/AuthContext';
import { useLanguageNavigation } from './hooks/useLanguageNavigation';

export default function SettingsPage() {
  const { t } = useTranslation();
  const { deleteAccount } = useAuth();
  const { navigateWithLang } = useLanguageNavigation();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newPublicName, setNewPublicName] = useState('');
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      const response = await getUserInfo();
      setUserInfo(response.user);
    } catch (error) {
      console.error('Error fetching user info:', error);
      setSnackbar({
        open: true,
        message: t('settings.failedToLoadUserInformation'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setNewPublicName(userInfo?.public_name || '');
    setEditDialogOpen(true);
  };

  const handleSavePublicName = async () => {
    if (!newPublicName.trim()) {
      setSnackbar({
        open: true,
        message: t('settings.publicNameCannotBeEmpty'),
        severity: 'error'
      });
      return;
    }

    try {
      setUpdating(true);
      await updatePublicName(newPublicName.trim());
      setUserInfo(prev => ({ ...prev, public_name: newPublicName.trim() }));
      setEditDialogOpen(false);
      setSnackbar({
        open: true,
        message: t('settings.publicNameUpdatedSuccessfully'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating public name:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('settings.failedToUpdatePublicName'),
        severity: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDeleteAccountClick = () => {
    setDeleteConfirmation('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      setSnackbar({
        open: true,
        message: 'Please type DELETE to confirm',
        severity: 'error'
      });
      return;
    }

    try {
      setDeleting(true);
      await deleteAccount();
      
      setSnackbar({
        open: true,
        message: t('settings.deleteAccountSuccess'),
        severity: 'success'
      });
      
      // Navigate to home page after successful deletion
      setTimeout(() => {
        navigateWithLang('');
      }, 2000);
      
    } catch (error) {
      console.error('Error deleting account:', error);
      setSnackbar({
        open: true,
        message: t('settings.deleteAccountError'),
        severity: 'error'
      });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setDeleteConfirmation('');
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('navigation.settings')}
        </Typography>
        
        {/* User Profile Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('settings.userProfile')}
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {t('settings.publicName')}
                  </Typography>
                  <Typography variant="h6">
                    {userInfo?.public_name || t('settings.notSet')}
                  </Typography>
                </Box>
                <IconButton 
                  onClick={handleEditClick}
                  color="primary"
                  size="small"
                >
                  <EditIcon />
                </IconButton>
              </Box>
              
              <Typography variant="body2" color="textSecondary">
                {t('settings.publicNameDescription')}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Preferences Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('settings.preferences')}
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('common.language')}
            </Typography>
            <LanguageSwitcher />
          </Box>
        </Paper>

        {/* Account Deletion Section */}
        <Paper sx={{ p: 3, mb: 3, border: '1px solid #f44336' }}>
          <Typography variant="h6" gutterBottom color="error">
            {t('settings.accountDeletion')}
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {t('settings.deleteAccountWarning')}
            </Typography>
            
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteAccountClick}
              disabled={deleting}
            >
              {t('settings.deleteAccount')}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Edit Public Name Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('settings.editPublicName')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('settings.publicName')}
            type="text"
            fullWidth
            variant="outlined"
            value={newPublicName}
            onChange={(e) => setNewPublicName(e.target.value)}
            placeholder={t('settings.enterPublicName')}
            disabled={updating}
            helperText={t('settings.publicNameHelperText')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={updating}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSavePublicName} 
            variant="contained" 
            disabled={updating || !newPublicName.trim()}
          >
            {updating ? <CircularProgress size={20} /> : t('settings.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleDeleteDialogClose} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle color="error">
          {t('settings.deleteAccount')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {t('settings.deleteAccountConfirmation')}
          </Typography>
          
          <Typography variant="body2" color="textSecondary" paragraph>
            {t('settings.deleteAccountWarning')}
          </Typography>
          
          <Typography variant="body2" paragraph>
            {t('settings.deleteAccountInstructions')}
          </Typography>
          
          <TextField
            fullWidth
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            placeholder={t('settings.deleteAccountPlaceholder')}
            variant="outlined"
            sx={{ mt: 2 }}
            disabled={deleting}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteDialogClose}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={deleting || deleteConfirmation !== 'DELETE'}
          >
            {deleting ? t('settings.deleteAccountInProgress') : t('settings.confirmDeletion')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
} 